<template>
    <RbacDisplayGuard
        required-any
        :features="expandableOneOfFeatures"
    >
        <Expandable label="Bulk Actions" :disabled="disabled || loading">
            <template #default="{ close }">
                <RbacDisplayGuard :features="[RbacFeature.dashboardPauseEntity]">
                    <ExpandableItem
                        v-if="showPause"
                        label="Pause"
                        :icon="faPauseCircle"
                        @click="setPauseStatus(true); close()"
                    />
                </RbacDisplayGuard>
                <RbacDisplayGuard :features="[RbacFeature.dashboardPauseEntity]">
                    <ExpandableItem
                        v-if="showUnpause"
                        label="Unpause"
                        :icon="faPlayCircle"
                        @click="setPauseStatus(false); close()"
                    />
                </RbacDisplayGuard>
                <RbacDisplayGuard :features="[RbacFeature.dashboardActivateDeactivateEntity]">
                    <ExpandableItem
                        v-if="showActivate"
                        label="Activate"
                        :icon="faPlayCircle"
                        @click="activate(); close()"
                    />
                </RbacDisplayGuard>
                <RbacDisplayGuard :features="[RbacFeature.dashboardActivateDeactivateEntity]">
                    <ExpandableItem
                        v-if="showDeactivate"
                        label="Deactivate"
                        :icon="faStopCircle"
                        @click="deactivate(); close()"
                    />
                </RbacDisplayGuard>
                <RbacDisplayGuard :features="[RbacFeature.dashboardCreateNewLink]">
                    <ExpandableItem
                        :disabled="!enableSendDocumentRequest"
                        :icon="faPaperPlane"
                        label="Create a New Link"
                        @click="$emit('sendDocumentRequest'); close()"
                    />
                </RbacDisplayGuard>
            </template>
        </Expandable>
    </RbacDisplayGuard>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faPaperPlane } from '@fortawesome/free-regular-svg-icons';
    import { faPauseCircle, faPlayCircle, faStopCircle } from '@fortawesome/free-solid-svg-icons';

    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import Expandable from '@/components/expandable/Expandable.vue';
    import ExpandableItem from '@/components/expandable-item/ExpandableItem.vue';
    import {
        DASHBOARD_BULK_ACTION_REQUIRED_ONE_OF_FEATURES,
    } from '@/modules/dashboard/constants/dashboardBulkActionRequiredOneOfFeatures.constant';

    @Component({
        components: { RbacDisplayGuard, Expandable, ExpandableItem },
        // PRODUCT-26110 Task 13: RbacDisplayGuard's render() returns `$slots.default?.()`
        // directly (always an array -> always a Fragment root, no matter how many
        // elements the slot contains -- see RbacDisplayGuard.vue). Since this component's
        // template root IS an RbacDisplayGuard, Vue 3's automatic attrs/listener
        // fallthrough tries to forward this component's own $attrs onto that root, then
        // fails to forward a second time onto the Fragment underneath it, producing
        // "Extraneous non-emits event listeners ... could not be automatically inherited
        // because component renders fragment or text root nodes" for every listener the
        // parent (EntitiesTable.vue) binds here that isn't declared below. Declaring
        // `emits` marks them consumed at THIS hop, so Vue never attempts (or needs) to
        // forward them further -- the actual delivery already worked via `this.$emit(...)`
        // regardless (that's instance-level, unrelated to attrs fallthrough), so this is a
        // console-warning/hygiene fix, not a functional-dead-listener fix.
        emits: [ 'bulkActivate', 'bulkDeactivate', 'bulkSetPauseState', 'sendDocumentRequest' ],
    })
    export default class BulkActionsExpandable extends Vue {
        private RbacFeature = RbacFeature;

        private faPauseCircle = faPauseCircle;
        private faPlayCircle = faPlayCircle;
        private faStopCircle = faStopCircle;
        private faPaperPlane = faPaperPlane;
        private expandableOneOfFeatures = DASHBOARD_BULK_ACTION_REQUIRED_ONE_OF_FEATURES;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Boolean, default: true })
        private showPause!: boolean;

        @Prop({ type: Boolean, default: true })
        private showUnpause!: boolean;

        @Prop({ type: Boolean, default: true })
        private showActivate!: boolean;

        @Prop({ type: Boolean, default: true })
        private showDeactivate!: boolean;

        @Prop({ type: Boolean, default: true })
        private enableSendDocumentRequest!: boolean;

        private setPauseStatus(value: boolean) {
            this.$emit('bulkSetPauseState', value);
        }

        private activate() {
            this.$emit('bulkActivate');
        }

        private deactivate() {
            this.$emit('bulkDeactivate');
        }
    }
</script>
